import React from 'react';
import {
  Modal,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ScrollView,
} from 'react-native';

import T from '../Components/T';
export interface UpdateModalProps {
  visible: boolean;
  isForceUpdate: boolean;
  currentVersion: string;
  latestVersion: string;
  releaseNotes?: string;
  onUpdate: () => void;
  onLater: () => void;
}

export const UpdateModal: React.FC<UpdateModalProps> = ({
  visible,
  isForceUpdate,
  currentVersion,
  latestVersion,
  releaseNotes,
  onUpdate,
  onLater,
}) => {
  if (!visible || Platform.OS === 'web') return null;

  return (
    <Modal
      transparent
      animationType="fade"
      visible={visible}
      onRequestClose={() => {
        if (!isForceUpdate) {
          onLater();
        }
      }}>
      <View style={styles.overlay}>
        <View style={styles.modalCard}>
          {/* Header Icon / Badge */}
          <View
            style={[
              styles.badgeContainer,
              isForceUpdate ? styles.forceBadge : styles.normalBadge,
            ]}>
            <Text style={styles.badgeText}>
              {isForceUpdate ? 'CRITICAL UPDATE' : 'NEW UPDATE'}
            </Text>
          </View>

          {/* Title */}
          <Text style={styles.title}>
            {isForceUpdate
              ? 'Update Required to Continue'
              : 'A New Version is Available!'}
          </Text>

          {/* Version Pills */}
          <View style={styles.versionRow}>
            <View style={styles.versionBadge}>
              <Text style={styles.versionLabel}><T>Current</T></Text>
              <Text style={styles.versionValue}>v{currentVersion}</Text>
            </View>
            <Text style={styles.arrow}>➜</Text>
            <View style={[styles.versionBadge, styles.latestVersionBadge]}>
              <Text style={styles.versionLabel}><T>New Version</T></Text>
              <Text style={styles.latestVersionValue}>v{latestVersion}</Text>
            </View>
          </View>

          {/* Release Notes */}
          {releaseNotes && (
            <View style={styles.notesContainer}>
              <Text style={styles.notesHeader}><T>What's New:</T></Text>
              <ScrollView style={styles.notesScrollView} nestedScrollEnabled>
                <Text style={styles.notesText}>{releaseNotes}</Text>
              </ScrollView>
            </View>
          )}

          {/* Description */}
          <Text style={styles.description}>
            {isForceUpdate
              ? 'Please update BlueHMS to the latest version to continue using the application.'
              : 'Update to get the latest features, performance improvements, and bug fixes.'}
          </Text>

          {/* Action Buttons */}
          <View style={styles.buttonRow}>
            {!isForceUpdate && (
              <TouchableOpacity
                style={[styles.button, styles.laterButton]}
                onPress={onLater}
                activeOpacity={0.8}>
                <Text style={styles.laterButtonText}><T>Remind Me Later</T></Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[
                styles.button,
                styles.updateButton,
                isForceUpdate && styles.fullWidthButton,
              ]}
              onPress={onUpdate}
              activeOpacity={0.85}>
              <Text style={styles.updateButtonText}><T>Update Now</T></Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.75)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    zIndex: 999999,
  },
  modalCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    width: '100%',
    maxWidth: 440,
    padding: 24,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.25,
    shadowRadius: 15,
    elevation: 20,
    ...(Platform.OS === 'web' && {
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    }),
  },
  badgeContainer: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 20,
    marginBottom: 12,
  },
  normalBadge: {
    backgroundColor: '#E0F2FE',
  },
  forceBadge: {
    backgroundColor: '#FEE2E2',
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.8,
    color: '#0284C7',
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#0F172A',
    textAlign: 'center',
    marginBottom: 16,
  },
  versionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
    gap: 8,
  },
  versionBadge: {
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    alignItems: 'center',
  },
  latestVersionBadge: {
    backgroundColor: '#EFF6FF',
    borderColor: '#3B82F6',
    borderWidth: 1,
  },
  versionLabel: {
    fontSize: 10,
    color: '#64748B',
    textTransform: 'uppercase',
    fontWeight: '600',
  },
  versionValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#475569',
  },
  latestVersionValue: {
    fontSize: 14,
    fontWeight: '700',
    color: '#2563EB',
  },
  arrow: {
    fontSize: 16,
    color: '#94A3B8',
    marginHorizontal: 4,
  },
  notesContainer: {
    width: '100%',
    backgroundColor: '#F8FAFC',
    borderRadius: 10,
    padding: 12,
    marginBottom: 16,
    maxHeight: 120,
    borderColor: '#E2E8F0',
    borderWidth: 1,
  },
  notesHeader: {
    fontSize: 12,
    fontWeight: '600',
    color: '#334155',
    marginBottom: 4,
  },
  notesScrollView: {
    maxHeight: 80,
  },
  notesText: {
    fontSize: 13,
    color: '#475569',
    lineHeight: 18,
  },
  description: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 20,
  },
  buttonRow: {
    flexDirection: 'row',
    width: '100%',
    gap: 12,
  },
  button: {
    flex: 1,
    height: 46,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fullWidthButton: {
    flex: 1,
  },
  laterButton: {
    backgroundColor: '#F1F5F9',
    borderWidth: 1,
    borderColor: '#CBD5E1',
  },
  laterButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#475569',
  },
  updateButton: {
    backgroundColor: '#2563EB',
  },
  updateButtonText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#FFFFFF',
  },
});

export default UpdateModal;
